import React, { Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback/CenteredSpinner';
import { FeatureToggleBanner, useFeatureToggle } from '@features/feature-rollout-toggle';
import { useFeatureFlag, useOnboardingStep } from '@features/user';
import { Box } from '@mui/material';

import { lazyWithRetry } from '../../utils/lazyWithRetry';

import { useEncounterToggleGraduation } from './hooks/useEncounterToggleGraduation';

// Lazy load both versions for code splitting and A/B testing
const EncounterViewV1 = lazyWithRetry(() => import('./components/v1/EncounterView'));
const EncounterViewV2 = lazyWithRetry(() => import('./components/v2/EncounterViewV2'));

/**
 * Encounter Page with Feature Toggle
 *
 * Main entry point for viewing/editing encounters.
 * Feature gate: feature_encounter_view_v2
 *
 * For users WITH encounters backend flag:
 * - v1 ↔ v2 toggle (existing behavior)
 *
 * For users WITHOUT encounters flag (V2 gate only):
 * - v2 with "switch back to classic" navigating to /record
 * - V1 is never shown (they came from /record, not V1)
 */
export const EncounterPage: React.FC = () => {
  const navigate = useNavigate();
  const encountersEnabled = useFeatureFlag('encounters');
  const generatedFirstNote = useOnboardingStep('generated_first_note');
  const isNewUser = !generatedFirstNote;

  const toggleConfig = {
    featureKey: 'feature_encounter_view_v2',
    showBanner: true,
    isNewUser,
    graduationDate: '2026-03-21',
    nudgeVersion: 1,
    bannerText: encountersEnabled
      ? {
          messageOnNew: "You're using the new recorder",
          messageOnClassic: "We've updated the recorder",
          switchToNewLabel: 'Switch to new',
          switchToClassicLabel: 'Not ready? Switch back',
        }
      : {
          messageOnNew: "You're using the new recorder",
          messageOnClassic: "We've updated the recorder",
          switchToNewLabel: 'Try the new recorder',
          switchToClassicLabel: 'Not ready? Go back to classic',
        },
  };

  const { showNew, showClassic, showBanner, switchToClassic } = useFeatureToggle(toggleConfig);
  const { handleSwitchToClassic } = useEncounterToggleGraduation();

  // Combine graduation reset with toggle switch
  // For non-encounters users, also navigate to /record
  const handleSwitchToClassicWithReset = () => {
    handleSwitchToClassic();
    switchToClassic();
    if (!encountersEnabled) {
      navigate('/record');
    }
  };

  // Safety redirect: if a non-encounters user lands here with "classic" preference
  // (e.g. direct URL navigation), send them to /record
  useEffect(() => {
    if (showClassic && !encountersEnabled) {
      navigate('/record', { replace: true });
    }
  }, [showClassic, encountersEnabled, navigate]);

  return (
    <Box
      sx={{
        height: '100%',
        pb: showBanner ? 10 : 0, // Add padding when banner is visible
      }}
    >
      <FeatureToggleBanner
        config={toggleConfig}
        onSwitchToClassic={handleSwitchToClassicWithReset}
      />
      <Suspense fallback={<CenteredSpinner />}>
        {showNew && <EncounterViewV2 />}
        {showClassic && encountersEnabled && <EncounterViewV1 />}
      </Suspense>
    </Box>
  );
};

export default EncounterPage;
